// @src/pre-deployment/src/components/TrackingService/components/TrackingProgressDisplay.js v1.1
// - Move formatModuleName and styles above component for no-use-before-define compliance
import React from 'react';

import { useLearningProgress } from '../hooks/useLearningProgress.js';

const formatModuleName = (key) => {
    return key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()).trim();
};

const styles = {
    container: { backgroundColor: '#1a1a2e', borderRadius: '12px', padding: '16px', color: '#ffffff', fontFamily: 'Inter, Roboto, sans-serif' },
    loading: { display: 'flex', alignItems: 'center', gap: '12px', padding: '24px' },
    loadingSpinner: { width: '16px', height: '16px', border: '2px solid rgba(255,165,0,0.3)', borderTop: '2px solid #FFA500', borderRadius: '50%', animation: 'spin 1s linear infinite' },
    loadingText: { color: '#888', fontSize: '13px' },
    error: { display: 'flex', alignItems: 'center', gap: '8px', backgroundColor: 'rgba(255,82,82,0.1)', border: '1px solid rgba(255,82,82,0.3)' },
    errorIcon: { fontSize: '16px' },
    errorText: { color: '#ff5252', fontSize: '13px' },
    empty: { textAlign: 'center', padding: '24px' },
    emptyText: { color: '#666', fontSize: '13px' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' },
    title: { margin: 0, fontSize: '14px', fontWeight: '600', color: '#ffffff' },
    chapterBadge: { backgroundColor: 'rgba(255,165,0,0.15)', color: '#FFA500', padding: '4px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '500' },
    progressSection: { marginBottom: '16px' },
    progressBar: { height: '8px', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '4px', overflow: 'hidden', marginBottom: '6px' },
    progressFill: { height: '100%', backgroundColor: '#FFA500', borderRadius: '4px', transition: 'width 0.3s ease' },
    progressText: { fontSize: '12px', color: '#888' },
    statusGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '16px' },
    statusItem: { display: 'flex', alignItems: 'center', gap: '6px', padding: '8px', backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '6px' },
    statusIcon: { fontSize: '12px' },
    statusLabel: { fontSize: '11px', color: '#aaa' },
    moduleSection: { borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '12px' },
    moduleTitle: { margin: '0 0 8px 0', fontSize: '12px', fontWeight: '500', color: '#888' },
    moduleList: { display: 'flex', flexDirection: 'column', gap: '4px' },
    moduleItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 8px', backgroundColor: 'rgba(255,255,255,0.03)', borderRadius: '4px' },
    moduleName: { fontSize: '12px', color: '#ccc' },
    moduleStatus: { fontSize: '12px' },
    moreModules: { fontSize: '11px', color: '#666', textAlign: 'center', padding: '4px' },
    compactContainer: { display: 'flex', alignItems: 'center', gap: '8px' },
    compactProgressBar: { flex: 1, height: '4px', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '2px', overflow: 'hidden' },
    compactProgressFill: { height: '100%', backgroundColor: '#FFA500', transition: 'width 0.3s ease' },
    compactText: { fontSize: '11px', color: '#888', minWidth: '32px' },
};

/**
 * TrackingProgressDisplay Component
 * Displays learning progress data for a chapter including module completion and status
 * 
 * @param {Object} props
 * @param {string} props.userId - User ID
 * @param {number|string} props.level - Level number
 * @param {number|string} props.chapter - Chapter number
 * @param {Object} props.style - Optional custom styles
 * @param {boolean} props.compact - Show compact view
 */
const TrackingProgressDisplay = ({
    userId,
    level,
    chapter,
    style = {},
    compact = false
}) => {
    const { data, loading, error } = useLearningProgress(userId, level, chapter);

    if (loading) {
        return (
            <div style={{ ...styles.container, ...styles.loading, ...style }}>
                <div style={styles.loadingSpinner} />
                <span style={styles.loadingText}>Loading progress...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ ...styles.container, ...styles.error, ...style }}>
                <span style={styles.errorIcon}>⚠️</span>
                <span style={styles.errorText}>Unable to load progress</span>
            </div>
        );
    }

    if (!data) {
        return (
            <div style={{ ...styles.container, ...styles.empty, ...style }}>
                <span style={styles.emptyText}>No progress data</span>
            </div>
        );
    }

    // Calculate overall progress
    const modules = data.modules || {};
    const moduleKeys = Object.keys(modules);
    const completedModules = moduleKeys.filter(key =>
        modules[key]?.completion_percentage === 100 || modules[key]?.completed
    );
    const progressPercent = moduleKeys.length > 0
        ? Math.round((completedModules.length / moduleKeys.length) * 100)
        : 0;

    if (compact) {
        return (
            <div style={{ ...styles.compactContainer, ...style }}>
                <div style={styles.compactProgressBar}>
                    <div style={{ ...styles.compactProgressFill, width: `${progressPercent}%` }} />
                </div>
                <span style={styles.compactText}>{progressPercent}%</span>
            </div>
        );
    }

    return (
        <div style={{ ...styles.container, ...style }}>
            {/* Header */}
            <div style={styles.header}>
                <h4 style={styles.title}>Chapter Progress</h4>
                <span style={styles.chapterBadge}>L{level} Ch{chapter}</span>
            </div>

            {/* Progress Bar */}
            <div style={styles.progressSection}>
                <div style={styles.progressBar}>
                    <div style={{ ...styles.progressFill, width: `${progressPercent}%` }} />
                </div>
                <span style={styles.progressText}>{progressPercent}% Complete</span>
            </div>

            {/* Status Indicators */}
            <div style={styles.statusGrid}>
                <div style={styles.statusItem}>
                    <span style={styles.statusIcon}>{data.chapterStarted ? '✓' : '○'}</span>
                    <span style={styles.statusLabel}>Started</span>
                </div>
                <div style={styles.statusItem}>
                    <span style={styles.statusIcon}>{data.chapterCompleted ? '✓' : '○'}</span>
                    <span style={styles.statusLabel}>Completed</span>
                </div>
                <div style={styles.statusItem}>
                    <span style={styles.statusIcon}>{data.chapterUnlocked ? '🔓' : '🔒'}</span>
                    <span style={styles.statusLabel}>{data.chapterUnlocked ? 'Unlocked' : 'Locked'}</span>
                </div>
            </div>

            {/* Module List */}
            {moduleKeys.length > 0 && (
                <div style={styles.moduleSection}>
                    <h5 style={styles.moduleTitle}>Modules</h5>
                    <div style={styles.moduleList}>
                        {moduleKeys.slice(0, 5).map(key => (
                            <div key={key} style={styles.moduleItem}>
                                <span style={styles.moduleName}>{formatModuleName(key)}</span>
                                <span style={styles.moduleStatus}>
                                    {modules[key]?.completed ? '✓' :
                                        modules[key]?.user_started ? '...' : '○'}
                                </span>
                            </div>
                        ))}
                        {moduleKeys.length > 5 && (
                            <div style={styles.moreModules}>
                                +{moduleKeys.length - 5} more
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default TrackingProgressDisplay;
